/**
 * 카카오 소셜 로그인 서비스
 */

declare global {
  interface Window {
    Kakao: any;
  }
}

const KAKAO_JS_KEY = 'dd3dc06113dfa8c20627e29159efce30';

// 환경별 Redirect URI 자동 선택
const REDIRECT_URI = (() => {
  // 운영 환경 (production build)
  if (process.env.NODE_ENV === 'production') {
    return 'https://studyroom.newattend.com/auth/kakao/callback';
  }

  // 로컬 개발 환경
  const hostname = window.location.hostname;

  // HTTPS 로컬 테스트 (local.faceattend.store)
  if (hostname === 'local.faceattend.store' || hostname === 'dev.faceattend.store') {
    return `https://${hostname}/auth/kakao/callback`;
  }

  // 기본 로컬 개발 (localhost)
  return 'http://localhost:3000/auth/kakao/callback';
})();


class KakaoAuthService {
  private initialized = false;

  /**
   * 카카오 SDK 초기화
   */
  initialize() {
    if (this.initialized) return;

    if (!window.Kakao) {
      console.error('Kakao SDK not loaded');
      return;
    }

    if (!window.Kakao.isInitialized()) {
      window.Kakao.init(KAKAO_JS_KEY);
    }

    this.initialized = true;
  }

  /**
   * 카카오 로그인
   */
  login() {
    this.initialize();

    if (!window.Kakao) {
      throw new Error('Kakao SDK not loaded');
    }

    return new Promise((resolve, reject) => {
      window.Kakao.Auth.login({
        success: (authObj: any) => {
          this.getUserInfo()
            .then(resolve)
            .catch(reject);
        },
        fail: (err: any) => {
          console.error('❌ 카카오 로그인 실패:', err);
          reject(err);
        },
      });
    });
  }

  /**
   * 사용자 정보 가져오기
   */
  getUserInfo(): Promise<any> {
    this.initialize();

    return new Promise((resolve, reject) => {
      window.Kakao.API.request({
        url: '/v2/user/me',
        success: (res: any) => {
          resolve({
            id: res.id,
            email: res.kakao_account?.email,
            name: res.kakao_account?.profile?.nickname,
            phone: res.kakao_account?.phone_number,
            profile_image: res.kakao_account?.profile?.profile_image_url,
          });
        },
        fail: (err: any) => {
          console.error('❌ 사용자 정보 조회 실패:', err);
          reject(err);
        },
      });
    });
  }

  /**
   * 카카오 로그아웃
   */
  logout(): Promise<void> {
    this.initialize();

    return new Promise((resolve, reject) => {
      if (!window.Kakao.Auth.getAccessToken()) {
        resolve();
        return;
      }

      window.Kakao.Auth.logout(() => {
        resolve();
      });
    });
  }

  /**
   * 연결 끊기 (탈퇴)
   */
  unlink(): Promise<void> {
    this.initialize();

    return new Promise((resolve, reject) => {
      window.Kakao.API.request({
        url: '/v1/user/unlink',
        success: () => {
          resolve();
        },
        fail: (err: any) => {
          console.error('❌ 연결 끊기 실패:', err);
          reject(err);
        },
      });
    });
  }

  /**
   * 액세스 토큰 확인
   */
  getAccessToken(): string | null {
    this.initialize();
    return window.Kakao.Auth.getAccessToken();
  }

  /**
   * 로그인 상태 확인
   */
  isLoggedIn(): boolean {
    this.initialize();
    return !!window.Kakao.Auth.getAccessToken();
  }
}

export default new KakaoAuthService();
